import React, { useState, useEffect } from 'react';
import { HeroScreenSize } from '../types/hero';

interface HeroBackgroundProps {
  imageScale: number;
  isMobile: boolean;
}

const getScreenSize = (): HeroScreenSize => {
  const width = window.innerWidth;
  if (width < 768) return 'sm';
  if (width < 1024) return 'md';
  return 'lg';
};

const getHeroImages = (screenSize: HeroScreenSize): string[] => {
  return [
    `/assets/hero/${screenSize}/${screenSize}-1.webp`,
    `/assets/hero/${screenSize}/${screenSize}-2.webp`,
    `/assets/hero/${screenSize}/${screenSize}-3.webp`,
    `/assets/hero/${screenSize}/${screenSize}-4.webp`
  ];
};

const HeroBackground: React.FC<HeroBackgroundProps> = ({ imageScale, isMobile }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [screenSize, setScreenSize] = useState<HeroScreenSize>(getScreenSize());
  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    const handleResize = () => {
      const newScreenSize = getScreenSize();
      if (newScreenSize !== screenSize) {
        setScreenSize(newScreenSize);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [screenSize]);

  useEffect(() => {
    setImages(getHeroImages(screenSize));
  }, [screenSize]);

  useEffect(() => {
    if (images.length === 0) return;

    // Preload all images
    Promise.all(
      images.map((src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    );

    const interval = setInterval(() => {
      setOpacity(0);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
        setOpacity(1);
      }, 300);
    }, 8000);

    return () => clearInterval(interval);
  }, [images]);

  if (images.length === 0) {
    return null;
  }

  return (
    <>
      <div className="absolute inset-0">
        <img 
          src={images[currentIndex]}
          alt="Sierpe Frogs Background"
          className="absolute inset-0 w-full h-full object-cover will-change-transform object-center"
          style={{ 
            transform: `scale(${imageScale})`,
            transition: 'transform 0.2s ease-out, opacity 300ms ease-in-out',
            transformOrigin: 'center center',
            opacity
          }}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-[#2A382A]"></div>
    </>
  );
};

export default HeroBackground;